<template>
  <section itemscope itemtype="http://schema.org/Course" class="course">
    <template v-if="isPageLoading">
      <CourseOverviewSkel :gradient="gradient" />
      <div class="course__content-wrapper">
        <CourseContentSkel />
      </div>
    </template>
    <template v-else>
      <div class="course__content">
        <router-view :key="$route.fullPath" />
      </div>
      <article
        class="course__background"
        :style="
          `background-image: url(${$getCourseBackground(
            course.id,
            'large'
          )}), linear-gradient(${gradient})`
        "
      >
        <div class="course__overview-wrapper">
          <Overview
            :course="course"
            :isAuthenticated="
              isUser || (isAuthenticated && course.has_sample_content)
            "
            :plansLink="plansLink"
            :isComingSoon="courseStatus === 'coming soon'"
            :isFree="course.has_sample_content"
          />
        </div>
        <div class="course__details-wrapper">
          <Details
            :course="course"
            :files="files"
            :isAuthenticated="
              isUser || (isAuthenticated && course.has_sample_content)
            "
          />
        </div>
      </article>
      <div class="course__content-wrapper">
        <div class="course__progress-wrapper">
          <Progress
            v-if="isUser && courseStatus !== 'coming soon'"
            :progressPercentage="progressPercentage"
            @resume="resumeCourse"
          />
        </div>
        <CourseContentSkel v-if="isContentLoading" />
        <div v-else class="course__group-wrapper">
          <Group
            v-for="(group, i) in course.content"
            :key="`course-group-${i}`"
            :group="group"
            :isUser="isUser"
            :progress="progress"
            :currentVideos="videos"
            :currentQuizzes="quizzes"
            :currentExercises="exercises"
            :currentLabs="labs"
            :currentIFrames="iframes"
          />
        </div>
      </div>
      <SendIssueModal v-if="showIssueModal" parent="course" />
      <transition name="fade" appear>
        <ProgressReviewModal
          v-if="showReviewModal"
          :progress="progressPercentage"
        />
      </transition>
    </template>
  </section>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { hasBaseAccess } from '@/helpers'
import CourseOverviewSkel from '@/components/loaders/skeletons/course/CourseOverviewSkel.vue'
import CourseContentSkel from '@/components/loaders/skeletons/course/CourseContentSkel.vue'
import Overview from '@/components/course/Overview.vue'
import Details from '@/components/course/Details.vue'
import Group from '@/components/course/content/Group.vue'
import Progress from '@/components/course/Progress.vue'
import SendIssueModal from '@/components/modals/SendIssueModal.vue'
import ProgressReviewModal from '@/components/modals/ProgressReviewModal.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'Course',
  components: {
    CourseOverviewSkel,
    CourseContentSkel,
    Overview,
    Details,
    Progress,
    Group,
    SendIssueModal,
    ProgressReviewModal,
  },
  metaInfo() {
    const { course } = this
    return {
      title: course && course.name ? `INE - ${course.name}` : `INE - Course`,
      meta: [
        {
          vmid: `course`,
          name: `description`,
          content: course && course.description ? course.description : '',
        },
      ],
      link: [
        {
          rel: 'canonical',
          href: `${CONSTANTS.MARKETING_WEBSITE_DIRECTORY_BASE_URL}/courses/${this.$route?.params?.slug}`,
        },
      ],
    }
  },
  data() {
    return {
      isLoading: true,
      levelUuids: null,
    }
  },
  computed: {
    ...mapState({
      isPageLoading: state => state.app.isPageLoading,
      isContentLoading: state => state.content.isContentLoading,
      isAuthenticated: state => state.auth.isAuthenticated,
      course: state => state.courses.currentCourse,
      allLevels: state => state.courses.currentLevels,
      videos: state => state.content.currentVideos,
      quizzes: state => state.content.currentQuizzes,
      exercises: state => state.content.currentExercises,
      labs: state => state.content.currentLabs,
      iframes: state => state.content.currentIFrames,
      progress: state => state.courses.currentCourseProgress,
      plansLink: state => state.app.plansLink,
      currentIssue: state => state.content.currentIssue,
      contentIssues: state => state.courses.contentIssues,
      focusedLabId: state => state.labs.focusedLabId,
      currentSubscriptions: state => state.subscriptions.currentSubscriptions,
      resumeExerciseId: state => state.exercises.resumeExerciseId,
    }),
    ...mapGetters({
      isFreeTrialUser: 'auth/isFreeTrialUser',
    }),
    courseStatus() {
      return this.course.status
    },
    progressPercentage() {
      const {
        progress: { progress },
      } = this
      if (progress) {
        return progress === 1 ? 100 : parseInt(progress * 100)
      }
      return 0
    },
    gradient() {
      return this.$getGradient()
    },
    hasProgress() {
      const { progress } = this
      return !!(progress && progress.is_active)
    },
    files() {
      const { files } = this.course
      return files || []
    },
    isUser() {
      return hasBaseAccess(this.course)
    },
    showIssueModal() {
      return this.currentIssue && this.currentIssue.content_type
    },
    showReviewModal() {
      const {
        course: { user_rating, user_status },
        progressPercentage,
      } = this
      if (!user_status || user_rating?.confirmed) return false
      const {
        prompt_review_50_perc_completion: isFiftyPercentComplete,
        prompt_review_90_perc_completion: isNinetyPercentComplete,
      } = user_status

      const isInWindowForPrompt =
        (progressPercentage >= 50 &&
          progressPercentage < 90 &&
          !isFiftyPercentComplete) ||
        (progressPercentage >= 90 &&
          progressPercentage < 100 &&
          !isNinetyPercentComplete)

      return isInWindowForPrompt
    },
  },
  methods: {
    ...mapMutations({
      setParentContent: 'content/CURRENT_PARENT',
    }),
    resumeCourse() {
      const {
        allLevels: levels,
        videos,
        quizzes,
        exercises,
        iframes,
        labs,
      } = this
      let tempVideo = null
      let tempIFrame = null
      let tempLab = null
      let tempQuizz = null
      let tempExerciseGroup = null

      let unstartedLevel = null
      let unstartedLevelType = null

      let index = 0

      while (!unstartedLevel && index < levels.length) {
        const level = levels[index]
        switch (level.content_type) {
          case CONSTANTS.CONTENT_TYPES.VIDEO:
            tempVideo = videos.find(
              video =>
                video.user_status !== CONSTANTS.CONTENT_USER_STATUS.FINISHED &&
                video.id === level.uuid
            )
            if (tempVideo) {
              unstartedLevel = tempVideo
              unstartedLevelType = CONSTANTS.CONTENT_TYPES.VIDEO
            }
            break
          case CONSTANTS.CONTENT_TYPES.QUIZ:
            tempQuizz = quizzes.find(
              quiz =>
                quiz.user_status !== CONSTANTS.CONTENT_USER_STATUS.FINISHED &&
                quiz.id === level.uuid
            )
            if (tempQuizz) {
              unstartedLevel = tempQuizz
              unstartedLevelType = CONSTANTS.CONTENT_TYPES.QUIZ
            }
            break
          case CONSTANTS.CONTENT_TYPES.EXERCISE_GROUP:
            this.levelUuids = level.exercises.map(e => e.uuid)
            tempExerciseGroup = exercises.find(this.checkExerciseInGroup)
            if (tempExerciseGroup) {
              unstartedLevel = level
              unstartedLevelType = CONSTANTS.CONTENT_TYPES.EXERCISE_GROUP
            }
            break
          case CONSTANTS.CONTENT_TYPES.IFRAME:
            tempIFrame = iframes.find(
              iframe =>
                iframe.user_status !== CONSTANTS.CONTENT_USER_STATUS.FINISHED &&
                iframe.id === level.uuid
            )
            if (tempIFrame) {
              unstartedLevel = tempIFrame
              unstartedLevelType = CONSTANTS.CONTENT_TYPES.IFRAME
            }
            break
          case CONSTANTS.CONTENT_TYPES.LAB:
            tempLab = labs.find(
              lab =>
                lab.user_status !== CONSTANTS.CONTENT_USER_STATUS.FINISHED &&
                lab.id === level.uuid
            )
            if (tempLab) {
              unstartedLevel = tempLab
              unstartedLevelType = CONSTANTS.CONTENT_TYPES.LAB
            }
            break
        }
        index++
      }

      switch (unstartedLevelType) {
        case CONSTANTS.CONTENT_TYPES.VIDEO:
          this.$store.dispatch('content/setPlayingVideoId', unstartedLevel.id)
          this.$store.dispatch('content/setPlayingVideoTime', 0)
          this.$store.dispatch('iframes/setPlayingIFrameId', '')
          this.$store.dispatch('content/setCurrentExercisesId', '')
          this.$store.dispatch('content/setCurrentQuizId', '')
          break
        case CONSTANTS.CONTENT_TYPES.QUIZ:
          this.$store.dispatch('content/setCurrentQuizId', unstartedLevel.id)
          this.$store.dispatch('content/setPlayingVideoId', '')
          this.$store.dispatch('content/setPlayingVideoTime', 0)
          this.$store.dispatch('iframes/setPlayingIFrameId', '')
          this.$store.dispatch('content/setCurrentExercisesId', '')
          break
        case CONSTANTS.CONTENT_TYPES.EXERCISE_GROUP:
          this.$store.dispatch('content/setCurrentQuizId', '')
          this.$store.dispatch('content/setPlayingVideoId', '')
          this.$store.dispatch('content/setPlayingVideoTime', 0)
          this.$store.dispatch('iframes/setPlayingIFrameId', '')
          this.$store.dispatch(
            'content/setCurrentExercisesId',
            unstartedLevel.uuid
          )
          break
        case CONSTANTS.CONTENT_TYPES.IFRAME:
          this.$store.dispatch('iframes/setPlayingIFrameId', unstartedLevel.id)
          this.$store.dispatch('content/setPlayingVideoId', '')
          this.$store.dispatch('content/setPlayingVideoTime', 0)
          this.$store.dispatch('content/setCurrentExercisesId', '')
          this.$store.dispatch('content/setCurrentQuizId', '')
          break
        default:
          // for Labs only scroll
          this.$scrollTo('.level:not(.finished)', 1000, {
            container: 'body',
            offset: -220,
          })
          break
      }
    },
    async fetchCourse(id) {
      const course = await this.$store.dispatch('courses/getCourseById', id)
      if (!course) {
        this.$router.push({
          name: 'NotFound',
          params: { pathMatch: this.$route.path },
        })
        throw new Error('NotFound')
      }
      this.setParentContent({ ...course, type: 'course' })
      if (this.isAuthenticated) {
        await this.$store.dispatch('courses/getActiveCourses')
        await this.$store.dispatch('content/getBookmarks')
        if (!this.contentIssues) {
          this.$store.dispatch('content/getContentIssues')
        }
      }
      this.$store.dispatch('app/setIsPageLoading', false)
      this.$store.dispatch('courses/resetCurrentLevels')
      await this.$store.dispatch('content/fetchContentForCourse', {
        course,
        isAuthenticated: this.isAuthenticated,
      })
      this.$store.dispatch('content/setIsContentLoading', false)

      await this.$nextTick()
      if (this.resumeExerciseId) {
        this.resumeCourse()
        this.$store.dispatch('exercises/setResumeExerciseId', '')
      }
    },
    async queryData(id) {
      this.$store.dispatch('app/setIsPageLoading', true)
      this.$store.dispatch('content/setIsContentLoading', true)
      try {
        await this.fetchCourse(id)
      } catch (error) {
        return error
      }
      await this.$nextTick()
      // If there is a focused Lab, scroll to it
      if (this.focusedLabId) {
        this.$scrollTo('.lab-focused', 1000, {
          container: 'body',
          offset: -220,
        })
        this.$store.dispatch('labs/setFocusLabId', '')
      }
    },
    checkExerciseInGroup(exercise) {
      return (
        exercise.user_status !== CONSTANTS.CONTENT_USER_STATUS.FINISHED &&
        this.levelUuids.includes(exercise.id)
      )
    },
  },
  created() {
    this.queryData(this.$route.params.id)
  },
  watch: {
    $route(to, from) {
      if (to.params.id !== from.params.id) {
        this.queryData(to.params.id)
      }
    },
    course(course) {
      this.sendMixpanelEvent(this.eventsConstants.PAGE_VISIT_COURSE, {
        course_id: course.id,
        course_name: course.name,
        course_learning_areas: course.learning_areas,
      })
    },
  },
  destroyed() {
    this.$store.dispatch('content/resetContentState')
  },
}
</script>

<style lang="scss">
.course {
  background-color: var(--dark-gray);
  padding: 0 0 10% 0;
  margin: 0 auto;
  &__background {
    margin: 0 auto;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    padding: 120px 0 0 0;
  }
  &__overview-wrapper {
    max-width: 1900px;
    padding: 0 15px 70px 15px;
    margin: 0 auto;
    @include tablet {
      padding: 0 100px 75px 100px;
    }
  }
  &__details-wrapper {
    margin: 0 auto;
    padding: 0;
    max-width: 1900px;
    @include desktop {
      padding: 0 100px;
    }
  }
  &__content-wrapper {
    background-color: var(--dark-gray);
    max-width: 1900px;
    padding: 0 5px;
    margin: 0 auto;
    @include tablet {
      padding: 150px 40px 0 40px;
    }
    @include desktop {
      padding: 150px 100px 0 100px;
    }
  }
  &__progress-wrapper {
    width: 95%;
    margin: 0 auto;
  }
  &__loader-wrapper {
    margin: 50px auto;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
